import { useEffect, useMemo, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { Search, Sparkles } from "lucide-react"
import type { AppShell } from "@/App"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { StatusBadge } from "@/components/StatusBadge"
import { ProposalKindChip, KIND_META } from "@/components/ProposalKindChip"
import {
  listProposals,
  type GeneratedBy,
  type ProposalKpis,
  type ProposalListRow,
  type SimulationStatus,
} from "@/lib/api"
import { formatEur, relativeFromNow } from "@/lib/format"
import { KIND_LABEL_PT, KIND_VALUES, detailPathForKind, type Kind } from "@/lib/kind-labels"
import { cn } from "@/lib/utils"

interface ListProps {
  shell: AppShell | null
}

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "__all__", label: "Todos os estados" },
  { value: "draft", label: "Draft" },
  { value: "under_review", label: "Em revisão" },
  { value: "sent", label: "Enviada" },
  { value: "ganho", label: "Ganho" },
  { value: "perdido", label: "Perdido" },
]

const GENERATED_BY_OPTIONS: Array<{ value: "__all__" | GeneratedBy; label: string }> = [
  { value: "__all__", label: "Todas as origens" },
  { value: "ai", label: "Geradas por IA" },
  { value: "user", label: "Comerciais" },
]

function isKind(v: string | null): v is Kind {
  return v != null && (KIND_VALUES as string[]).includes(v)
}

export function List({ shell }: ListProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const kindFilter: Kind | null = (() => {
    const k = searchParams.get("kind")
    return isKind(k) ? k : null
  })()

  const [items, setItems] = useState<ProposalListRow[]>([])
  const [kpis, setKpis] = useState<ProposalKpis | null>(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState<string>("__all__")
  const [seller, setSeller] = useState<string>("__all__")
  const [generatedBy, setGeneratedBy] = useState<"__all__" | GeneratedBy>("__all__")
  const [debouncedSearch, setDebouncedSearch] = useState("")

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  // Listing filtrado (search + status + kind) — usado para tabela principal.
  useEffect(() => {
    if (!shell) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    listProposals(shell, {
      kind: kindFilter ?? undefined,
      search: debouncedSearch || undefined,
      status: status === "__all__" ? undefined : (status as SimulationStatus),
      generated_by: generatedBy === "__all__" ? undefined : generatedBy,
      created_by: seller === "__all__" ? undefined : seller,
      limit: 100,
    })
      .then((res) => {
        if (cancelled) return
        setItems(res.items)
      })
      .catch((err) => console.error("[list] load failed", err))
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [shell, debouncedSearch, status, generatedBy, seller, kindFilter])

  // Cartões por módulo e vendedores do filtro: contados no SERVIDOR sobre todas
  // as propostas visíveis. Antes contavam-se as linhas de um pedido que o
  // servidor corta em 200 — com 535 propostas os cartões somavam 200.
  useEffect(() => {
    if (!shell) return
    let cancelled = false
    listProposals(shell, { include_kpis: true, limit: 1 })
      .then((res) => {
        if (!cancelled) setKpis(res.kpis ?? null)
      })
      .catch((err) => console.error("[list] kpis load failed", err))
    return () => {
      cancelled = true
    }
  }, [shell])

  const kpisByKind = useMemo(() => {
    const zero = { total: 0, this_month: 0 }
    const by = kpis?.by_kind ?? {}
    return {
      solar: by.solar ?? zero,
      ac: by.ac ?? zero,
      bc_aqs: by.bc_aqs ?? zero,
      bc_aq: by.bc_aq ?? zero,
    } as Record<Kind, { total: number; this_month: number }>
  }, [kpis])

  // Vendedores distintos (para o filtro) — nomes só para admins (o backend só
  // os resolve nesse caso).
  const sellers = useMemo(
    () => (kpis?.sellers ?? []).map((s) => ({ value: s.id, label: s.name })),
    [kpis],
  )
  const showSellerFilter = shell?.isAdmin === true && sellers.length > 1

  // Filtro por vendedor aplicado client-side sobre a lista já filtrada.
  // O vendedor filtra-se no servidor (created_by).
  const shown = items

  const empty = useMemo(() => !loading && shown.length === 0, [loading, shown])

  function setKindFilter(next: Kind | null) {
    const params = new URLSearchParams(searchParams)
    if (next == null) params.delete("kind")
    else params.set("kind", next)
    setSearchParams(params, { replace: true })
  }

  return (
    <div className="space-y-4">
      {/* KPIs por kind */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {KIND_VALUES.map((kind) => {
          const meta = KIND_META[kind]
          const Icon = meta.Icon
          const k = kpisByKind[kind]
          return (
            <Card key={kind}>
              <CardContent className="flex flex-col gap-1 p-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Icon className={cn("h-4 w-4", meta.color)} />
                  {KIND_LABEL_PT[kind]}
                </div>
                <div className="text-2xl font-semibold tracking-tight">
                  {k.total}
                </div>
                <div className="text-xs text-muted-foreground">
                  Este mês: {k.this_month}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>Propostas</CardTitle>
            <div className="flex flex-wrap gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Pesquisar..."
                  className="pl-8"
                />
              </div>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={generatedBy} onValueChange={(v) => setGeneratedBy(v as "__all__" | GeneratedBy)}>
                <SelectTrigger className="w-44" aria-label="Origem da proposta">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GENERATED_BY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {showSellerFilter && (
                <Select value={seller} onValueChange={setSeller}>
                  <SelectTrigger className="w-44">
                    <SelectValue placeholder="Vendedor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos os vendedores</SelectItem>
                    {sellers.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Kind filter chips */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={kindFilter == null ? "default" : "outline"}
              size="sm"
              onClick={() => setKindFilter(null)}
            >
              Todos
            </Button>
            {KIND_VALUES.map((k) => {
              const meta = KIND_META[k]
              const Icon = meta.Icon
              const active = kindFilter === k
              return (
                <Button
                  key={k}
                  variant={active ? "default" : "outline"}
                  size="sm"
                  onClick={() => setKindFilter(k)}
                  className="gap-1"
                >
                  <Icon className={cn("h-3.5 w-3.5", !active && meta.color)} />
                  {KIND_LABEL_PT[k]}
                </Button>
              )
            })}
          </div>
        </CardHeader>
        <CardContent className="px-0">
          {loading ? (
            <div className="px-6 py-10 text-center text-sm text-muted-foreground">
              A carregar propostas…
            </div>
          ) : empty ? (
            <div className="px-6 py-10 text-center text-sm text-muted-foreground">
              Sem resultados.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Número</TableHead>
                  <TableHead>Cliente</TableHead>
                  {showSellerFilter && <TableHead>Vendedor</TableHead>}
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Preço</TableHead>
                  <TableHead className="text-right">Payback</TableHead>
                  <TableHead>Criado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map((row) => {
                  const kind = (row.kind ?? "solar") as Kind
                  const linkTo = detailPathForKind(kind, row.id)
                  return (
                    <TableRow key={row.id}>
                      <TableCell>
                        <ProposalKindChip kind={kind} />
                      </TableCell>
                      <TableCell>
                        {linkTo ? (
                          <Link
                            to={linkTo}
                            className="font-medium text-accent hover:underline"
                          >
                            {row.number}
                          </Link>
                        ) : (
                          <span className="font-medium">{row.number}</span>
                        )}
                        {row.generated_by === "ai" && (
                          <Badge variant="info" className="ml-2" title="Proposta gerada por IA (formulário do site)">
                            <Sparkles className="h-3 w-3" />
                            IA
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{row.client_name}</TableCell>
                      {showSellerFilter && (
                        <TableCell className="text-muted-foreground">
                          {row.created_by_name ?? "—"}
                        </TableCell>
                      )}
                      <TableCell>
                        <StatusBadge status={row.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        {formatEur(row.total_price_eur)}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {row.payback_years != null
                          ? `${Number(row.payback_years).toFixed(1)} anos`
                          : "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {relativeFromNow(row.created_at)}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
